import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Trash2, Plus } from "lucide-react";
import { api, errMsg, FIELD, AREA, fmtDate, fmtTime, fmtMoney } from "@/lib/api";
import { Field } from "@/components/common/Field";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const EMPTY = { title: "", description: "", category: "Workshop", date: "", time: "18:00", location: "Virtual", capacity: 30, fee: 0 };

const EventForm = ({ onDone }) => {
  const [f, setF] = useState(EMPTY);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/events", { ...f, capacity: Number(f.capacity), fee: Number(f.fee) || 0 }); toast.success("Event created"); setF(EMPTY); onDone(); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <form onSubmit={submit} className="border border-white/10 p-6 grid sm:grid-cols-4 gap-4" data-testid="admin-event-form">
      <Field label="Title" required className="sm:col-span-2"><Input className={FIELD} required value={f.title} onChange={set("title")} data-testid="admin-event-title-input" /></Field>
      <Field label="Category"><Input className={FIELD} value={f.category} onChange={set("category")} data-testid="admin-event-category-input" /></Field>
      <Field label="Price ($, 0 = free)"><Input className={FIELD} type="number" min={0} step="0.01" value={f.fee} onChange={set("fee")} data-testid="admin-event-price-input" /></Field>
      <Field label="Date" required><Input className={FIELD} type="date" required value={f.date} onChange={set("date")} data-testid="admin-event-date-input" /></Field>
      <Field label="Time" required><Input className={FIELD} type="time" required value={f.time} onChange={set("time")} data-testid="admin-event-time-input" /></Field>
      <Field label="Location"><Input className={FIELD} value={f.location} onChange={set("location")} data-testid="admin-event-location-input" /></Field>
      <Field label="Capacity"><Input className={FIELD} type="number" min={1} value={f.capacity} onChange={set("capacity")} data-testid="admin-event-capacity-input" /></Field>
      <Field label="Description" className="sm:col-span-4"><Textarea className={AREA} value={f.description} onChange={set("description")} data-testid="admin-event-description-input" /></Field>
      <div className="sm:col-span-4"><button className="btn-gold !py-3" data-testid="admin-event-submit-button"><Plus className="w-4 h-4" /> Create Event</button></div>
    </form>
  );
};

export const EventManager = () => {
  const qc = useQueryClient();
  const { data = [] } = useQuery({ queryKey: ["events"], queryFn: () => api.get("/events").then((r) => r.data) });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["events"] }); qc.invalidateQueries({ queryKey: ["admin"] }); };
  const remove = async (id) => {
    if (!window.confirm("Delete this event and its registrations?")) return;
    try { await api.delete(`/admin/events/${id}`); toast.success("Event deleted"); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-6">
      <EventForm onDone={refresh} />
      <div className="grid gap-3" data-testid="admin-events-list">
        {data.map((e) => (
          <div key={e.id} className="border border-white/10 p-5 flex justify-between items-center gap-4" data-testid={`admin-event-${e.id}`}>
            <div><p className="font-medium">{e.title}</p><p className="text-xs text-white/50 mt-1">{fmtDate(e.date)} · {fmtTime(e.time)} · {e.location} · {e.fee ? fmtMoney(e.fee) : "Free"} · {e.registered}/{e.capacity} registered</p></div>
            <button onClick={() => remove(e.id)} className="text-white/40 hover:text-red-400 transition-colors" data-testid={`admin-event-delete-${e.id}`} aria-label="Delete event"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
};
